import type { AlertSettings, DashboardHandoff } from "../shared/contracts";
import {
  parseBrowserObservationCommand,
  parseBrowserObservationResult,
  type BrowserObservationCommand,
  type BrowserObservationResult,
} from "../shared/browser-observation";
import {
  HOSTED_APP_SOURCE,
  HOSTED_EXTENSION_SOURCE,
  HOSTED_PROTOCOL_VERSION,
  type AgentToolSection,
  type HostedRequest,
  type HostedResponse,
  type HostedSessionResetEvent,
  type HostedFocusEvent,
  type HostedDrawerVisibilityEvent,
} from "../shared/hosted-bridge";

const extensionId = new URLSearchParams(location.search).get("bridge");
export const hostedParentOrigin =
  extensionId && /^[a-p]{32}$/.test(extensionId) && window.parent !== window
    ? `chrome-extension://${extensionId}`
    : null;

export function isHostedSessionReset(event: MessageEvent): boolean {
  const data = event.data as Partial<HostedSessionResetEvent> | null;
  return Boolean(
    hostedParentOrigin &&
    event.source === window.parent &&
    event.origin === hostedParentOrigin &&
    data?.source === HOSTED_EXTENSION_SOURCE &&
    data.version === HOSTED_PROTOCOL_VERSION &&
    data.type === "session-reset",
  );
}

export function isHostedFocusEvent(event: MessageEvent): boolean {
  const data = event.data as Partial<HostedFocusEvent> | null;
  return Boolean(
    hostedParentOrigin &&
    event.source === window.parent &&
    event.origin === hostedParentOrigin &&
    data?.source === HOSTED_EXTENSION_SOURCE &&
    data.version === HOSTED_PROTOCOL_VERSION &&
    data.type === "focus-tools" &&
    Object.keys(data).length === 3,
  );
}

export function isHostedDrawerVisibilityEvent(
  event: MessageEvent,
): event is MessageEvent<HostedDrawerVisibilityEvent> {
  const data = event.data as Partial<HostedDrawerVisibilityEvent> | null;
  return Boolean(
    hostedParentOrigin &&
    event.source === window.parent &&
    event.origin === hostedParentOrigin &&
    data &&
    typeof data === "object" &&
    data.source === HOSTED_EXTENSION_SOURCE &&
    data.version === HOSTED_PROTOCOL_VERSION &&
    data.type === "drawer-visibility" &&
    typeof data.visible === "boolean" &&
    Object.keys(data).length === 4,
  );
}

type Command = HostedRequest["type"];
export class HostedBridgeError extends Error {
  constructor(
    message: string,
    public status = 0,
  ) {
    super(message);
  }
}
type CommandResult<T extends Command> = T extends "connect"
  ? DashboardHandoff
  : T extends "browser-observation"
    ? BrowserObservationResult
    : T extends "alert-settings"
      ? AlertSettings
      : undefined;

export function hostedCommand<T extends Command>(
  type: T,
  fields: T extends "connect"
    ? { challenge: string }
    : T extends "alert-settings"
      ? { settings?: AlertSettings }
      : T extends "browser-observation"
        ? { command: BrowserObservationCommand }
        : T extends "open-tools"
          ? { section: AgentToolSection }
          : T extends "surface-ready"
            ? { ready: boolean }
            : Record<string, never>,
  signal?: AbortSignal,
): Promise<CommandResult<T>> {
  if (!hostedParentOrigin)
    return Promise.reject(new Error("Open GHL Connect from the extension."));
  const requestId = crypto.randomUUID();
  if (
    type === "browser-observation" &&
    !parseBrowserObservationCommand((fields as { command: unknown }).command)
  )
    return Promise.reject(new Error("Invalid browser observation command."));
  const message = {
    source: HOSTED_APP_SOURCE,
    version: HOSTED_PROTOCOL_VERSION,
    requestId,
    type,
    ...fields,
  } as HostedRequest;
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener("message", receive);
      signal?.removeEventListener("abort", abort);
      window.clearTimeout(timeout);
    };
    const abort = () => {
      cleanup();
      reject(new DOMException("Cancelled", "AbortError"));
    };
    const receive = (event: MessageEvent) => {
      if (event.source !== window.parent || event.origin !== hostedParentOrigin)
        return;
      const data = event.data as Partial<HostedResponse> | null;
      if (
        !data ||
        data.source !== HOSTED_EXTENSION_SOURCE ||
        data.version !== HOSTED_PROTOCOL_VERSION ||
        data.type !== type ||
        data.requestId !== requestId ||
        typeof data.ok !== "boolean"
      )
        return;
      cleanup();
      if (!data.ok) {
        reject(
          new HostedBridgeError(
            typeof data.error === "string" && data.error
              ? data.error.slice(0, 1000)
              : "Extension connection unavailable.",
            typeof data.status === "number" &&
              Number.isInteger(data.status) &&
              data.status >= 400 &&
              data.status <= 599
              ? data.status
              : 0,
          ),
        );
        return;
      }
      if (type === "connect") {
        const handoff = data.data as DashboardHandoff | undefined;
        if (
          !handoff ||
          typeof handoff.code !== "string" ||
          !handoff.code ||
          handoff.code.length > 512 ||
          typeof handoff.expiresAt !== "number" ||
          !Number.isFinite(handoff.expiresAt) ||
          Object.keys(handoff).length !== 2 ||
          handoff.expiresAt <= Date.now()
        ) {
          reject(new Error("GHL connection expired. Try again."));
          return;
        }
      } else if (type === "alert-settings") {
        const settings = data.data as AlertSettings | undefined;
        if (
          !settings ||
          Object.keys(settings).length !== 2 ||
          typeof settings.desktopNotifications !== "boolean" ||
          typeof settings.alertSounds !== "boolean"
        ) {
          reject(new Error("Could not load alert settings."));
          return;
        }
      } else if (type === "browser-observation") {
        let parsed: BrowserObservationResult | null;
        try {
          parsed = parseBrowserObservationResult(data.data);
        } catch {
          parsed = null;
        }
        if (
          !parsed ||
          parsed.action !==
            (fields as { command: BrowserObservationCommand }).command.action
        ) {
          reject(new Error("Invalid browser observation response."));
          return;
        }
        resolve(parsed as CommandResult<T>);
        return;
      } else if (data.data !== undefined) {
        reject(new Error("Invalid extension response."));
        return;
      }
      resolve(data.data as CommandResult<T>);
    };
    const timeout = window.setTimeout(
      () => {
        cleanup();
        reject(
          new Error("The GHL Connect extension did not respond. Try again."),
        );
      },
      type === "connect" ? 45_000 : 15_000,
    );
    window.addEventListener("message", receive);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
    else window.parent.postMessage(message, hostedParentOrigin);
  });
}
